import { useMemo, useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { flashFeed } from '@/mocks/gossip';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { dotClass, tagClass } from '@/pages/home/components/tagStyle';

const FILTERS = ['全部', '明星八卦', '网红黑料', '今日吃瓜', '热门事件'];

export default function FlashFeed() {
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('全部');
  const [order, setOrder] = useState<'new' | 'hot'>('new');

  const items = useMemo(() => {
    const keyword = query.trim();
    let list = flashFeed.filter((item) => (activeTag === '全部' ? true : item.tag === activeTag));
    if (keyword) {
      list = list.filter((item) => item.title.includes(keyword) || item.summary.includes(keyword));
    }
    if (order === 'hot') {
      list = [...list].sort((a, b) => b.heatValue - a.heatValue);
    }
    return list;
  }, [query, activeTag, order]);

  return (
    <section id="feed" className="border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="01 / Live Feed"
          title="今日快讯流"
          desc="按时间往下滑，今天发生的每一件热门事都排在同一条线上。搜一搜、筛一筛，只看你想看的那口。"
        />

        <div className="mb-8 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-3 lg:flex-row lg:items-center">
          <div className="relative w-full lg:w-72 lg:shrink-0">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜一搜，比如“顶流”“直播间”"
              className="w-full rounded-md border border-background-300/70 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>

          <div className="flex flex-1 flex-wrap items-center gap-1.5">
            {FILTERS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  activeTag === tag
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300/70 bg-background-50 text-foreground-600 hover:border-secondary-300 hover:text-secondary-800'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-md border border-background-300/70 bg-background-50 p-1">
            {(['new', 'hot'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setOrder(mode)}
                className={`cursor-pointer whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${
                  order === mode ? 'bg-foreground-900 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {mode === 'new' ? '最新' : '最热'}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-emotion-unhappy-line text-2xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-500">这口瓜暂时没找到，换个关键词试试？</p>
          </div>
        ) : (
          <div className="relative">
            <span className="absolute bottom-4 left-[5px] top-4 w-px bg-background-200" aria-hidden="true"></span>
            <div className="space-y-4">
              {items.map((item, i) => (
                <Reveal key={item.id} delay={i * 40}>
                  <article className="relative pl-7">
                    <span className={`absolute left-0 top-6 h-[11px] w-[11px] rounded-full border-2 ${dotClass(item.tag)}`} aria-hidden="true"></span>
                    <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 md:flex-row md:items-center md:gap-5 md:p-5">
                      <div className="h-40 w-full shrink-0 overflow-hidden rounded-md md:h-[84px] md:w-[84px]">
                        <img src={item.img} alt={`${item.tag}：${item.title}`} title={`${item.title} · 91吃瓜`} className="h-full w-full object-cover object-top" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-heading text-xs font-bold text-foreground-800">{item.time}</span>
                          <span className={`rounded border px-2 py-0.5 text-[11px] ${tagClass(item.tag)}`}>{item.tag}</span>
                          {item.fresh ? (
                            <span className="flex items-center gap-1 text-[11px] font-medium text-primary-600">
                              <i className="ri-flashlight-fill text-[11px]" aria-hidden="true"></i>
                              刚刚更新
                            </span>
                          ) : null}
                        </div>
                        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">{item.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                      </div>

                      <div className="flex shrink-0 items-center gap-5 border-t border-background-200 pt-3 md:flex-col md:items-end md:gap-1.5 md:border-l md:border-t-0 md:pl-5 md:pt-0">
                        <div className="text-right">
                          <p className="font-heading text-sm font-bold text-primary-700">{item.heat}</p>
                          <p className="text-[11px] text-foreground-400">热度</p>
                        </div>
                        <div className="text-right">
                          <p className="font-heading text-sm font-bold text-foreground-800">{item.comments}</p>
                          <p className="text-[11px] text-foreground-400">条讨论</p>
                        </div>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-center">
          <button
            type="button"
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-refresh-line mr-1.5 align-middle text-sm" aria-hidden="true"></i>
            刷新看看有没有新瓜
          </button>
        </div>
      </div>
    </section>
  );
}